import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, useContext, useMemo } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { DataReportsProvider } from 'libs.data';
import { FeatureFlagsContext, FlagContext } from 'libs.flags.react';
import { NotFoundPage } from 'libs.react';
import { AuthContext, ProductTemplatesProvider, useUserRoles } from 'libs.react_internal';
import { StudyWrapper } from './components/study_wrapper/study_wrapper.component';
import { STUDY_MANAGEMENT_FLAGS } from './constants/developer_flags.constants';
import { LibraryActivitiesProvider } from './contexts/library_activities/library_activities.context';
import { StudiesProvider } from './contexts/studies/studies.context';
import { StudyConfigurationProvider } from './contexts/study_configuration/study_configuration.context';
import { StudyTestPlanProvider } from './contexts/test_plans/test_plans.context';
const ReviewPage = lazy(() => import('./pages/review/review.page'));
const ScheduleTimelinePage = lazy(() => import('./pages/schedule_timeline/schedule_timeline.page'));
const StudiesDashboardPage = lazy(() => import('./pages/studies_dashboard/studies_dashboard.page'));
const StudyBuildPage = lazy(() => import('./pages/study_build/study_build.page'));
const ResourcesDashboardPage = lazy(() => import('./pages/resources/resources_dashboard.page'));
const ResourceViewPage = lazy(() => import('./pages/resources/resource_view.page'));
const TranslationPage = lazy(() => import('./pages/translations/translations.page'));
const TranslationAssetsViewPage = lazy(() => import('./pages/translations/translation_assets_view.page'));
const TranslationAssetsReviewPage = lazy(() => import('./pages/translations/translation_assets_review.page'));
const StudySummaryPage = lazy(() => import('./pages/study_summary/study_summary.page'));
const ConsentViewPage = lazy(() => import('./pages/consent/consent_view.page'));
const ConsentCompiledTemplatePreviewPage = lazy(() => import('./pages/consent/consent_template_preview.page'));
const IRBViewPage = lazy(() => import('./pages/irb/irb_view.page'));
const VersionsPage = lazy(() => import('./pages/versions/versions.page'));
const VersionViewPage = lazy(() => import('./pages/versions/version_view.page'));
const StudyTestsPage = lazy(() => import('./pages/study_tests/study_tests.page'));
const StudyTestPlaygroundPage = lazy(() => import('./pages/study_test_plans/study_testplan_playground.page'));
const StudyTestPlanViewPage = lazy(() => import('./pages/study_test_plans/study_testplan_view.page'));
const StudyTestPlansPage = lazy(() => import('./pages/study_test_plans/study_test_plans.page'));
const StudyDeployPage = lazy(() => import('./pages/study_deploy/study_deploy.page'));
const StudyTestsViewPage = lazy(() => import('./pages/study_tests/study_test_view.page'));
const StudyDeployViewPage = lazy(() => import('./pages/study_deploy/study_deploy_view.page'));
const NotificationsDashboardPage = lazy(() => import('./pages/study_notifications/study_notifications_dashboard.page'));
const ExtensionsPage = lazy(() => import('./pages/extensions/extensions.page'));
const ExtensionViewPage = lazy(() => import('./pages/extensions/extension_view.page'));
const StudyDashboardsPage = lazy(() => import('./pages/study_dashboards/study_dashboards.page'));
const StudyDeployViewSitePage = lazy(() => import('./pages/study_deploy/study_deploy_view_site.page'));
const StudyTestViewSitePage = lazy(() => import('./pages/study_tests/study_test_view_site.page'));
const StudySitesPage = lazy(() => import('./pages/study_sites/study_sites.page'));
// Data pages
const ReportsListPage = lazy(() => import('./pages/data_reports/reports_list.page'));
const ReportDashboardPage = lazy(() => import('./pages/data_reports/report_dashboard.page'));
const DataReportsViewPage = lazy(() => import('./pages/data_reports/data_reports_view.page'));
const DataReportsBuildPage = lazy(() => import('./pages/data_reports/data_reports_build.page'));
const DataTransferPage = lazy(() => import('./pages/data_transfer/data_transfer.page'));
const ExecutionDetailsPage = lazy(() => import('./pages/data_transfer/execution_details.page'));
const DataChangeAccessPage = lazy(() => import('./pages/data_change/data_change_access.page'));
const StudyManagerPage = lazy(() => import('./pages/study_manager/study_manager_access.page'));
const UserTrainingReportPage = lazy(() => import('./pages/user_training/user_training.page'));
const ProvidersWrapper = ({ children }) => {
    return (_jsx(StudiesProvider, { children: _jsx(ProductTemplatesProvider, { children: _jsx(DataReportsProvider, { children: _jsx(StudyConfigurationProvider, { children: _jsx(LibraryActivitiesProvider, { children: _jsx(StudyTestPlanProvider, { children: children }) }) }) }) }) }));
};
export const StudyManagementApp = () => {
    const navigate = useNavigate();
    const flagManager = useContext(FlagContext);
    const { isInternalUser } = useContext(AuthContext);
    const { isFeatureEnabled } = useContext(FeatureFlagsContext);
    const { isAdmin, isBuilder, isUserAndSiteManager, isDataUser, isDataAdmin, isDataManager, isSiteUser } = useUserRoles();
    const isViewReportsEnabled = flagManager.getValue(STUDY_MANAGEMENT_FLAGS.view_reports);
    const isBuildReportsEnabled = flagManager.getValue(STUDY_MANAGEMENT_FLAGS.build_reports);
    const isChartsEnabled = isFeatureEnabled('LibraryReportsBuilder');
    const isConsentEnabled = isFeatureEnabled('DocGenEConsent');
    const isStudySitesEnabled = isFeatureEnabled('StudySites');
    const gatedRoutes = useMemo(() => {
        const routes = [];
        if (isAdmin || isBuilder) {
            routes.push(_jsx(Route, { path: 'build', element: _jsx(StudyBuildPage, {}) }));
            routes.push(_jsx(Route, { path: 'build/schedule', element: _jsx(ScheduleTimelinePage, {}) }));
            routes.push(_jsx(Route, { path: 'build/resources', element: _jsx(ResourcesDashboardPage, {}) }));
            routes.push(_jsx(Route, { path: 'build/resources/:resourceId', element: _jsx(ResourceViewPage, {}) }));
            routes.push(_jsx(Route, { path: 'build/extensions', element: _jsx(ExtensionsPage, {}) }));
            routes.push(_jsx(Route, { path: 'build/extensions/:extensionId', element: _jsx(ExtensionViewPage, {}) }));
            if (isChartsEnabled) {
                routes.push(_jsx(Route, { path: 'build/dashboards', element: _jsx(StudyDashboardsPage, {}) }));
            }
            routes.push(_jsx(Route, { path: 'build/study-summary', element: _jsx(StudySummaryPage, {}) }));
            if (flagManager.getValue(STUDY_MANAGEMENT_FLAGS.review)) {
                routes.push(_jsx(Route, { path: 'build/review', element: _jsx(ReviewPage, {}) }));
            }
            routes.push(_jsx(Route, { path: 'translations', element: _jsx(TranslationPage, {}) }));
            routes.push(_jsx(Route, { path: 'translations/:assetId', element: _jsx(TranslationAssetsViewPage, {}) }));
            routes.push(_jsx(Route, { path: 'translations/:assetId/review/:locale?', element: _jsx(TranslationAssetsReviewPage, {}) }));
            routes.push(_jsx(Route, { path: 'versions', element: _jsx(VersionsPage, {}) }));
            routes.push(_jsx(Route, { path: 'versions/:versionId', element: _jsx(VersionViewPage, {}) }));
            if (isConsentEnabled) {
                routes.push(_jsx(Route, { path: 'consent', element: _jsx(ConsentViewPage, {}) }));
                routes.push(_jsx(Route, { path: 'consent-template-preview', element: _jsx(ConsentCompiledTemplatePreviewPage, {}) }));
            }
            routes.push(_jsx(Route, { path: 'review-package', element: _jsx(IRBViewPage, {}) }));
            if (isInternalUser) {
                routes.push(_jsx(Route, { path: 'build/notifications', element: _jsx(NotificationsDashboardPage, {}) }));
                routes.push(_jsx(Route, { path: 'test-plans', element: _jsx(StudyTestPlansPage, {}) }));
                routes.push(_jsx(Route, { path: 'test-plans/:testPlanId/playground', element: _jsx(StudyTestPlaygroundPage, {}) }));
                routes.push(_jsx(Route, { path: 'test-plans/:testPlanId', element: _jsx(StudyTestPlanViewPage, {}) }));
            }
        }
        if (isAdmin || isBuilder || isUserAndSiteManager) {
            if (isStudySitesEnabled) {
                routes.push(_jsx(Route, { path: 'study-sites', element: _jsx(StudySitesPage, {}) }));
                routes.push(_jsx(Route, { path: 'study-sites/:envId/sites/:siteId', element: _jsx(StudyDeployViewSitePage, {}) }));
            }
            routes.push(_jsx(Route, { path: 'tests', element: _jsx(StudyTestsPage, {}) }));
            routes.push(_jsx(Route, { path: 'tests/:envId', element: _jsx(StudyTestsViewPage, {}) }));
            routes.push(_jsx(Route, { path: 'tests/:envId/sites/:siteId', element: _jsx(StudyTestViewSitePage, {}) }));
            routes.push(_jsx(Route, { path: 'deploy', element: _jsx(StudyDeployPage, {}) }));
            routes.push(_jsx(Route, { path: 'deploy/:envId', element: _jsx(StudyDeployViewPage, {}) }));
            routes.push(_jsx(Route, { path: 'deploy/:envId/sites/:siteId', element: _jsx(StudyDeployViewSitePage, {}) }));
        }
        /*
          Report details page - available to admins, data users, user/site managers and site users,
          this should be updated as part of URP-275
        */
        if (isAdmin || isDataUser || isUserAndSiteManager || isSiteUser) {
            routes.push(_jsx(Route, { path: 'data/reports/:reportId', element: _jsx(ReportDashboardPage, {}) }));
        }
        // Reports routes - available to admins, data users, and user/site managers
        if (isAdmin || isDataUser || isUserAndSiteManager) {
            routes.push(_jsx(Route, { path: 'data/reports', element: _jsx(ReportsListPage, {}) }));
            routes.push(_jsx(Route, { path: 'data/study-manager', element: _jsx(StudyManagerPage, {}) }));
            routes.push(_jsx(Route, { path: 'data/user-training', element: _jsx(UserTrainingReportPage, {}) }));
        }
        // Other data routes - available to admins and data administrators/managers only (not data reviewers)
        if (isAdmin || isDataAdmin || isDataManager) {
            routes.push(_jsx(Route, { path: 'data/data-transfers', element: _jsx(DataTransferPage, {}) }));
            routes.push(_jsx(Route, { path: 'data/data-transfers/:executionId', element: _jsx(ExecutionDetailsPage, {}) }));
            routes.push(_jsx(Route, { path: 'data/data-changes', element: _jsx(DataChangeAccessPage, {}) }));
        }
        if (isAdmin) {
            if (isViewReportsEnabled) {
                routes.push(_jsx(Route, { path: 'data/view-reports', element: _jsx(DataReportsViewPage, {}) }));
            }
            if (isBuildReportsEnabled) {
                routes.push(_jsx(Route, { path: 'data/build-reports', element: _jsx(DataReportsBuildPage, {}) }));
            }
        }
        return routes;
    }, [
        flagManager,
        isAdmin,
        isBuilder,
        isUserAndSiteManager,
        isInternalUser,
        isViewReportsEnabled,
        isBuildReportsEnabled,
        isChartsEnabled,
        isConsentEnabled,
        isStudySitesEnabled,
        isDataUser,
        isDataAdmin,
        isDataManager,
        isSiteUser,
    ]);
    return (_jsx(ProvidersWrapper, { children: _jsxs(Routes, { children: [!isSiteUser && _jsx(Route, { index: true, path: 'dashboard', element: _jsx(StudiesDashboardPage, {}) }), _jsxs(Route, { path: ':studyId', element: _jsx(StudyWrapper, {}), children: [...gatedRoutes] }), _jsx(Route, { path: '*', element: _jsx(NotFoundPage, { goBack: () => navigate(-1) }) })] }) }));
};
export { STUDY_MANAGEMENT_FLAGS } from './constants/developer_flags.constants';
